import { Box, useTheme } from "@mui/material";
import { type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { type ColumnDefinition, DefaultDataGridSlots, Typography } from "@pmag/ui-components";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { AvailabilityStatusCell } from "@src/pages/subGroupDetail/subgroupDetailsListSection/availabilityStatusCell/AvailabilityCell";
import { PriceCell } from "@src/pages/subGroupDetail/subgroupDetailsListSection/priceCell/PriceCell";
import { type WishlistEntryModel } from "@src/pages/wishlist/Wishlist.utils";
import { type SPFArticleWithPriceInfo } from "@src/utils/events";
import type * as React from "react";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { ArticleImage, ArticleInfoBox, StyledDataGrid, StyledTypography } from "./Wishlist.styles";

interface WishlistProps {
    loading: boolean;
    wishlistEntries: WishlistEntryModel[];
}

enum WishlistField {
    ArticleInfo = "articleInfo",
    Model = "model",
    Price = "price",
}

export const Wishlist: React.FunctionComponent<WishlistProps> = ({ wishlistEntries, loading }) => {
    const intl = useIntl();
    const theme = useTheme();
    const { hasArticleInfos, quantityInfoDisclaimer } = useAttributeConfig();

    const columns = useMemo<Array<ColumnDefinition<"", "">>>(() => {
        let columns: Array<ColumnDefinition<"", "">> = [
            {
                align: "left",
                field: WishlistField.ArticleInfo,
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.wishlist.headerName.articleInfo",
                    defaultMessage: "Article / Art. No.",
                }),
                type: "string",
                minWidth: 250,
                sortable: false,
                filterable: false,
                flex: 1,
                renderCell: ({ row: sparePart }: GridRenderCellParams<WishlistEntryModel, WishlistEntryModel>) => (
                    <ArticleInfoBox>
                        {sparePart.article?.assetPath && <ArticleImage src={sparePart.article.assetPath} />}

                        <Box>
                            <Typography variant="heading-09">
                                {sparePart.article?.name ?? "-"}

                                {sparePart.quantity > 0 && (
                                    <StyledTypography component="span" variant="body-short-01" color={theme.palette.core.grey._60}>
                                        {`(${sparePart.quantity})`}

                                        {quantityInfoDisclaimer && <sup>**</sup>}
                                    </StyledTypography>
                                )}
                            </Typography>

                            <Typography variant="helper-text-01">{sparePart.article?.articleNumber}</Typography>
                        </Box>
                    </ArticleInfoBox>
                ),
            },
            {
                align: "left",
                field: WishlistField.Model,
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.wishlist.headerName.model",
                    defaultMessage: "Model",
                }),
                type: "string",
                minWidth: 160,
                sortable: false,
                filterable: false,
                flex: 1,
                renderCell: ({ row: wishlistEntry }: GridRenderCellParams<WishlistEntryModel, WishlistEntryModel>) => {
                    let modelDescription = `${wishlistEntry.model.year}`;
                    if (wishlistEntry.model.modelSpecifications)
                        modelDescription += wishlistEntry.model.modelSpecifications.map((item) => ` / ${item.name}`);
                    modelDescription += ` / ${wishlistEntry.model.articleNumber}`;

                    return (
                        <Box>
                            <Typography
                                variant="helper-text-01"
                                color={theme.palette.core.grey._80}
                            >{`${wishlistEntry.model.brand?.name} ${wishlistEntry.model.description}`}</Typography>

                            <Typography variant="helper-text-01" color={theme.palette.core.grey._80}>
                                {modelDescription}
                            </Typography>
                        </Box>
                    );
                },
            },
        ];
        if (hasArticleInfos) {
            columns = columns.concat([
                {
                    align: "left",
                    field: "availabilityStatus",
                    headerName: intl.formatMessage({
                        id: "sparePartsFinder.subgroupPage.sparePartsList.headerName.stockStatus",
                        defaultMessage: "Stock status",
                    }),
                    type: "string",
                    minWidth: 165,
                    sortable: false,
                    filterable: false,
                    resizable: false,
                    renderCell: ({ row: sparePart }: GridRenderCellParams<SPFArticleWithPriceInfo, SPFArticleWithPriceInfo>) => (
                        <AvailabilityStatusCell sparePart={sparePart} />
                    ),
                },
            ]);
        }
        columns = columns.concat([
            {
                align: "left",
                field: WishlistField.Price,
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.wishlist.headerName.price",
                    defaultMessage: "Price",
                }),
                type: "string",
                minWidth: 320,
                sortable: false,
                filterable: false,
                flex: 1,
                renderCell: ({ row: wishlistEntry }: GridRenderCellParams<WishlistEntryModel, WishlistEntryModel>) => (
                    <PriceCell
                        modelId={wishlistEntry.model.id}
                        item={wishlistEntry}
                        brand={wishlistEntry.model.brand?.name ?? ""}
                        showCellInWishlist={true}
                        clientWishlist={[]}
                    />
                ),
            },
        ]);

        return columns;
    }, [intl, theme, hasArticleInfos, quantityInfoDisclaimer]);

    return (
        <StyledDataGrid
            rows={wishlistEntries}
            columns={columns}
            slots={{
                ...DefaultDataGridSlots,
                toolbar: null,
            }}
            loading={loading}
            hideFooter
            autoHeight
        />
    );
};
